<template>
   <!-- WHAT: The UI for specifying the lake exports date and timestamp formats -->
   <!-- HISTORY:
      V250202.1: Fixed a bug that kept timePart2 and timePart3 fields disabled.
      V250616.1: 1st release.
   -->
   <!--
      Sample inputs: %y-%-m-%-d OR %Y/%m/%d %H:%M:%S
      Specifier Meaning Example:
         %Y  4-digit year          2023
         %y  2-digit year          23
         %m  Month (01-12)         07
         %d  Day of month (01-31)  09
         %H  Hour (00-23)          14
         %M  Minute (00-59)        30
         %S  Second (00-59)        45
 -->
<v-card flat>
   <v-card-text class="px-0 pb-2 pt-0">
      <v-expansion-panels focusable v-model="panel">
         <v-expansion-panel readonly>
            <v-expansion-panel-header hide-actions class="pl-3">
               <strong>{{hasTime ? 'Timestamp' : 'Date'}} Format</strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="px-0 py-5">
               <v-form lazy-validation
                  ref="form"
                  v-model="isFormValid"
               >
                  <v-row>
                     <v-col cols="12" class="px-1 py-0">
                        <v-select clearable dense persistent-hint
                           ref="datePart1"
                           autocomplete="off"
                           placeholder="Date Part 1..."
                           :items="datePart1Items"
                           v-model="formData.datePart1"
                           @change="datePart1Changed"
                           @click:clear="resetForm"
                        ></v-select>
                     </v-col>
                  </v-row>
                  <v-row>
                     <v-col cols="12" class="px-1 pt-3 pb-0">
                        <v-select dense persistent-hint
                           ref="datePart2"
                           autocomplete="off"
                           placeholder="Date Part 2..."
                           :disabled="!formData.datePart1"
                           :items="datePart2Items"
                           :rules="dropdownRules"
                           v-model="formData.datePart2"
                           @change="datePart2Changed"
                        ></v-select>
                     </v-col>
                  </v-row>
                  <v-row>
                     <v-col cols="12" class="px-1 pt-3 pb-0">
                        <v-select dense persistent-hint
                           ref="datePart3"
                           autocomplete="off"
                           placeholder="Date Part 3..."
                           :disabled="!formData.datePart2"
                           :items="datePart3Items"
                           :rules="dropdownRules"
                           v-model="formData.datePart3"
                        ></v-select>
                     </v-col>
                  </v-row>
                  <v-row v-if="hasTime">
                     <v-col cols="4" class="px-1 pt-3 pb-0">
                        <v-select dense persistent-hint
                           ref="hours"
                           autocomplete="off"
                           placeholder="Hours..."
                           :disabled="!formData.datePart3"
                           :items="timePart1Items"
                           :rules="dropdownRules"
                           v-model="formData.timePart1"
                        ></v-select>
                     </v-col>
                  <!-- </v-row>
                  <v-row v-if="hasTime"> -->
                     <v-col cols="4" class="px-1 pt-3 pb-0">
                        <v-select dense persistent-hint
                           ref="minutes"
                           autocomplete="off"
                           placeholder="Minutes..."
                           :disabled="!formData.timePart1"
                           :items="timePart2Items"
                           :rules="dropdownRules"
                           v-model="formData.timePart2"
                        ></v-select>
                     </v-col>
                  <!-- </v-row>
                  <v-row v-if="hasTime"> -->
                     <v-col cols="4" class="px-1 pt-3 pb-0">
                        <v-select dense persistent-hint
                           ref="seconds"
                           autocomplete="off"
                           placeholder="Seconds..."
                           :disabled="!formData.timePart2"
                           :items="timePart3Items"
                           :rules="dropdownRules"
                           v-model="formData.timePart3"
                        ></v-select>
                     </v-col>
                  </v-row>
                  <v-row>
                     <v-col xs="12" sm="12" :md="hasTime?4:12" class="px-1 pt-4 pb-0">
                        <v-select dense persistent-hint
                           ref="dateSeparator"
                           autocomplete="off"
                           placeholder="Date Separator..."
                           :hint="formData.dateSeparator?'Date Separator':''"
                           :disabled="!formData.datePart3"
                           :items="dateSeparatorItems"
                           :rules="dropdownRules"
                           v-model="formData.dateSeparator"
                           @change="dateSeparatorChanged"
                        ></v-select>
                     </v-col>
                     <v-col v-if="hasTime" xs="12" sm="12" md="4" class="px-1 pt-4 pb-0">
                        <v-select dense persistent-hint disabled
                           ref="timeSeparator"
                           autocomplete="off"
                           placeholder="Time Separator..."
                           :hint="formData.timeSeparator?'Time Separator':''"
                           :items="timeSeparatorItems"
                           :rules="dropdownRules"
                           v-model="formData.timeSeparator"
                        ></v-select>
                     </v-col>
                     <v-col v-if="hasTime" xs="12" sm="12" md="4" class="px-1 pt-4 pb-0">
                        <v-select dense persistent-hint
                           ref="dateTimeSeparator"
                           autocomplete="off"
                           placeholder="Date/Time Separator"
                           :hint="formData.dateTimeSeparator?'Date & Time Separator':''"
                           :disabled="!formData.timeSeparator"
                           :items="dateTimeSeparatorItems"
                           :rules="dropdownRules"
                           v-model="formData.dateTimeSeparator"
                        ></v-select>
                     </v-col>
                  </v-row>
               </v-form>
            </v-expansion-panel-content>
         </v-expansion-panel>
      </v-expansion-panels>
   </v-card-text>
</v-card>
</template>

<script>
const NAME = "BtDateFormatDefiner";

class FormData {
   constructor(initVal) {
      this.datePart1 = initVal?.datePart1 || '';
      this.datePart2 = initVal?.datePart2 || '';
      this.datePart3 = initVal?.datePart3 || '';
      this.timePart1 = initVal?.timePart1 || '';
      this.timePart2 = initVal?.timePart2 || '';
      this.timePart3 = initVal?.timePart3 || '';
      this.dateSeparator = initVal?.dateSeparator || '';
      this.timeSeparator = initVal?.timeSeparator || '';
      this.dateTimeSeparator = initVal?.dateTimeSeparator || '';
   }
}

export default {
   name: NAME,

   props: {
      value: {
         type: String,
         default: ''
      },
      debug: {
         type: Boolean,
         default: false
      },
      hasTime: {
         type: Boolean,
         default: false
      },
      initForm: {
         type: Boolean,
         default: false
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || 'Value is required!'
         },
         isFormValid: true,
         panel: 0,
         formData: new FormData(),
         datePart1Items: [
            { text: 'Year (4-digit: 2025)', value: 'Y'},
            { text: 'Year (2-digit: 25)', value: 'y'},
            { text: 'Month (with padded zero: 01-12)', value: 'm'},
            { text: 'Month (without padded zero: 1-12)', value: '-m'},
            { text: 'Day (with padded zero: 01-31)', value: 'd'},
            { text: 'Day (without padded zero: 1-31)', value: '-d'}
         ],
         datePart2Items: [],
         datePart3Items: [],
         timePart1Items: [
            { text: 'Hours: 00-23', value: 'H'},
            { text: 'Hours: 0-23', value: '-H'}
         ],
         timePart2Items: [
            { text: 'Minutes: 00-59', value: 'M'},
            { text: 'Minutes: 0-59', value: '-M'}
         ],
         timePart3Items: [
            { text: 'Seconds: 00-59', value: 'S'},
            { text: 'Seconds: 0-59', value: '-S'}
         ],
         dateSeparatorItems: [
            { text: 'Slash (/)', value: '/'},
            { text: 'Dash (-)', value: '-'}
         ],
         timeSeparatorItems: [
            { text: 'Colon (:)', value: ':'}
         ],
         dateTimeSeparatorItems: [
            { text: 'Space', value: ' '},
            { text: 'T', value: 'T'}
         ],
         dropdownRules: [],
         isFormChanged: false
      }
   },

   computed: {
      isFormValidOrChanged() {
         return [this.isFormValid, this.isFormChanged];
      }
   },

   watch: {
      initForm: {
         immediate: true,
         handler (val) {
            if (!val) return;

            const formData = new FormData();
            if (Object.keys(this.value).length) {
               if (this.hasTime) {
                  const [, date, separator, time] = this.value.match(/(.+?)([T ])(.+)/);
                  formData.dateTimeSeparator = separator;
                  formData.timeSeparator = ':';
                  this.parseDateFormat(formData, date);
                  const timeParts = time.split('%').filter(part => part);
                  for (let index = 0; index < timeParts.length; index++) {
                     formData['timePart' + (index + 1)] = timeParts[index].replace(':', '');// + '_' + timeParts[0];
                  }
               } else {
                  this.parseDateFormat(formData, this.value);
               }
            }

            this.formData = formData;
            this.$nextTick(() => {
               this.isFormChanged = false;
            });
         }
      },

      formData: {
         deep: true,
         immediate: false,
         handler (val) {
            this.isFormValid = this.$refs.form.validate();
            if (!this.isFormValid || !val.datePart2) return;

            this.isFormChanged = true;
            let format = '';
            if (val.datePart1) {
               const keys = Object.keys(val);
               keys.filter(k => k.startsWith('datePart')).filter(k => val[k]).forEach((key, i) => {
                  format += `%${val[key]}${i != 2 ? val.dateSeparator : ''}`;
               });

               if (this.hasTime) {
                  format += val.dateTimeSeparator
                  keys.filter(k => k.startsWith('timePart')).forEach((key, i) => {
                     format += `%${val[key]}${i != 2 ? val.timeSeparator : ''}`;
                  });
               }
            }

            this.$emit('input', format);
         }
      },

      isFormValidOrChanged() {
         this.$emit('change', this.isFormValid && this.isFormChanged);
      }
   },

   methods: {
      log(msg, always =false) {
         if (this.debug || always) {
            console.log(`-----${NAME} V250202.1 says => ${msg}`);
            // alert(`${NAME} V250202.1 says:\n${msg}`);
         }
      },

      parseDateFormat(formData, dateFormat) {
         const dateParts = dateFormat.split('%').filter(part => part);
         formData.dateSeparator = dateParts[0].charAt(dateParts[0].length - 1);
         for (let index = 0; index < dateParts.length; index++) {
            const datePart = index === 2 ? dateParts[index] : dateParts[index].slice(0, -1);
            formData['datePart' + (index + 1)] = datePart;
            if (index === 0)
               this.datePart1Changed(formData.datePart1)
            else if (index === 1)
               this.datePart2Changed(formData.datePart2)
         }
      },

      datePart1Changed(val) {
         if (val) {
            if (!this.dropdownRules.length) {
               this.dropdownRules = [this.rules.required];
            }
            this.datePart2Items = this.datePart1Items.filter(item => item.value.replace('-', '').toLowerCase() != val.replace('-', '').toLowerCase());
         } else {
            this.datePart2Items = [];
         }

         this.formData.datePart2 = '';
         this.datePart2Changed();
      },

      datePart2Changed(val) {
         this.datePart3Items = val ? this.datePart2Items.filter(item => item.value.replace('-', '').toLowerCase() != val.replace('-', '').toLowerCase()) : [];
         this.formData.datePart3 = '';
      },

      dateSeparatorChanged() {
         if (this.hasTime)
            this.formData.timeSeparator = this.timeSeparatorItems[0].value;
      },

      resetForm() {
         this.formData = new FormData();
         this.dropdownRules = [];
         this.$emit('input', '');
         this.isFormChanged = true;
      }
   },

   created() { },

   mounted() {
      // alert(`in ${NAME}.mounted()`);
   }
}
</script>

<style scope>
   .v-expansion-panel-header, .v-expansion-panel-header--active {
   /* padding: 0 16px; */
   min-height: 24px !important;
}
::v-deep .v-expansion-panel-content__wrap {
    padding: 4px 8px 4px !important;
}

</style>